// The soundtrack's song list (public/music/soundtrack.json) and the pure helpers around it: the play order and
// the stand-in cover for a song without one.

/** One song, as listed in public/music/soundtrack.json. */
export interface Song {
  file: string; // the MP3 in public/music
  title: string;
  artist: string;
  cover?: string; // an image in public/music/covers (optional)
  intro?: boolean; // the song that opens the game on the title screen (then it joins the shuffle)
}

const MUSIC_DIR = `${import.meta.env.BASE_URL}music/`;
// The songs are licensed and never published with the site: the live game streams them from the music Worker
// (Cloudflare R2, see music-worker/), and the dev server plays the local copies in public/music
const MUSIC_HOST: string = import.meta.env.VITE_MUSIC_URL ?? (import.meta.env.PROD ? 'https://hsfhc-music.protheus99.workers.dev' : '');
export const songUrl = (song: Song) => (MUSIC_HOST ? `${MUSIC_HOST}/music/` : MUSIC_DIR) + encodeURIComponent(song.file);
export const coverUrl = (song: Song) => (song.cover ? `${MUSIC_DIR}covers/${encodeURIComponent(song.cover)}` : null);

/**
 * The songs in soundtrack.json: its "songs" list (the file also holds instructions and examples, which don't play),
 * or a bare list. Entries without a file, title and artist are skipped.
 */
export function parseSoundtrack(json: unknown): Song[] {
  const list = Array.isArray(json) ? json : json && typeof json === 'object' ? (json as { songs?: unknown }).songs : undefined;
  if (!Array.isArray(list)) return [];
  return list.filter((s): s is Song => !!s && typeof s.file === 'string' && typeof s.title === 'string' && typeof s.artist === 'string');
}

/** The song list (empty when the file is missing or isn't valid JSON). */
export async function loadSoundtrack(): Promise<Song[]> {
  try {
    const response = await fetch(`${MUSIC_DIR}soundtrack.json`);
    return response.ok ? parseSoundtrack(await response.json()) : [];
  } catch {
    return [];
  }
}

/** The song that opens the game: the first one flagged as the intro, if any. */
export const introSong = (songs: Song[]): Song | null => songs.find((s) => s.intro === true) ?? null;

/**
 * The next round of songs: every song once, shuffled, and never the song that just played first (so a reshuffle
 * can't repeat it back to back).
 */
export function shuffledRound(songs: Song[], justPlayed: Song | null, random: () => number = Math.random): Song[] {
  const round = [...songs];
  for (let i = round.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [round[i], round[j]] = [round[j], round[i]];
  }
  if (round.length > 1 && justPlayed && round[0].file === justPlayed.file) round.push(round.shift()!);
  return round;
}

const SMALL_WORDS = new Set(['a', 'an', 'and', 'the', 'of', 'in', 'on', 'at', 'to', 'for', 'or']);

/** A song's initials for a cover-less pop-up: "Friday Lights" -> "FL", "Fourth and Long" -> "FL", "Overtime" -> "OV". */
export function songInitials(title: string): string {
  const all = title
    .replace(/\(.*?\)/g, ' ')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  const meaningful = all.filter((w) => !SMALL_WORDS.has(w.toLowerCase()));
  const words = meaningful.length > 0 ? meaningful : all;
  if (words.length === 0) return '♪';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

const COVER_COLORS = ['#B91C1C', '#1D4ED8', '#047857', '#7C3AED', '#C2410C', '#0E7490', '#A21CAF', '#4D7C0F'];

/** The same color for the same song, every time it plays. */
export function songColor(title: string): string {
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) % 1000003;
  return COVER_COLORS[h % COVER_COLORS.length];
}
